import { type FetchResult, fromPromise, Observable } from "@apollo/client";
import { onError } from "@apollo/client/link/error";
import { GRAPHQL_ERROR_CODES } from "@/lib/graphql/error-handler";
import { type AuthTokens, authService } from "@/lib/services/auth.service";
import { useAuthStore } from "@/lib/store/auth-enhanced";

const REFRESHABLE_ERROR_CODES: string[] = [
  GRAPHQL_ERROR_CODES.TOKEN_EXPIRED,
  GRAPHQL_ERROR_CODES.TOKEN_INVALID,
  GRAPHQL_ERROR_CODES.SESSION_EXPIRED,
  GRAPHQL_ERROR_CODES.UNAUTHORIZED,
];

type RefreshResult =
  | { status: "ok"; accessToken: string }
  | { status: "failed" }
  | { status: "superseded" };

let refreshPromise: Promise<RefreshResult> | null = null;

const runRefresh = async (): Promise<RefreshResult> => {
  const refreshToken = useAuthStore.getState().refreshToken;

  if (!refreshToken) {
    return { status: "failed" };
  }

  let tokens: AuthTokens;
  try {
    tokens = await authService.refreshToken(refreshToken);
  } catch {
    return useAuthStore.getState().refreshToken === refreshToken
      ? { status: "failed" }
      : { status: "superseded" };
  }

  const current = useAuthStore.getState();
  if (!current.isAuthenticated || current.refreshToken !== refreshToken) {
    return { status: "superseded" };
  }

  current.updateTokens(tokens);
  return { status: "ok", accessToken: tokens.accessToken };
};

const refreshOnce = (): Promise<RefreshResult> => {
  if (!refreshPromise) {
    useAuthStore.getState().setRefreshing(true);

    refreshPromise = runRefresh().finally(() => {
      refreshPromise = null;
      useAuthStore.getState().setRefreshing(false);
    });
  }

  return refreshPromise;
};

export const resetRefreshState = () => {
  refreshPromise = null;
};

const bearer = (token: string | null | undefined) => (token ? `Bearer ${token}` : "");

export const tokenRefreshLink = onError(({ graphQLErrors, networkError, operation, forward }) => {
  const context = operation.getContext();

  if (context.skipAuthRefresh) {
    return;
  }

  const hasExpiredTokenError =
    graphQLErrors?.some((error) => {
      const code = error.extensions?.code;
      return typeof code === "string" && REFRESHABLE_ERROR_CODES.includes(code);
    }) ||
    (!!networkError && "statusCode" in networkError && networkError.statusCode === 401);

  if (!hasExpiredTokenError) {
    return;
  }

  const store = useAuthStore.getState();

  if (!store.isAuthenticated) {
    return;
  }

  if (context.isRefreshRetry) {
    store.logout("Session expired");
    return;
  }

  const sentAuthorization = context.headers?.authorization;
  if (sentAuthorization && sentAuthorization !== bearer(store.accessToken)) {
    operation.setContext({ isRefreshRetry: true });
    return forward(operation);
  }

  return fromPromise(refreshOnce()).flatMap((result) => {
    if (result.status === "failed") {
      useAuthStore.getState().logout("Session expired");
    }

    if (result.status !== "ok") {
      return new Observable<FetchResult>((observer) => {
        observer.error(networkError ?? new Error("Session expired"));
      });
    }

    operation.setContext({ isRefreshRetry: true });
    return forward(operation);
  });
});
